import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  Upload, 
  Settings, 
  CreditCard, 
  Users,
  PlusCircle,
  ChevronRight,
  LogOut,
  Menu,
  X,
  Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';

const PRO_NAV_ITEMS = [
  { path: '/app', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/app/bids', icon: FileText, label: 'Bids' },
  { path: '/app/bids/new', icon: PlusCircle, label: 'New Bid' },
  { path: '/app/upload', icon: Upload, label: 'Upload' },
  { path: '/app/settings', icon: Settings, label: 'Settings' },
  { path: '/app/billing', icon: CreditCard, label: 'Billing' },
];

import BetaBanner from '../components/BetaBanner';

export const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const isPro = user?.plan === 'pro' || user?.is_platform_admin;
  const navItems = isPro ? PRO_NAV_ITEMS : [
    { path: '/app/settings', icon: Settings, label: 'Settings' },
    { path: '/app/billing', icon: CreditCard, label: 'Billing' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <Zap className="w-6 h-6 text-orange-500" />
          <span className="font-bold text-lg" style={{ fontFamily: 'Barlow Condensed' }}>BIDSENTRY</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white border-r border-slate-200 z-40
        transform transition-transform duration-200
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center gap-2 px-6 border-b border-slate-200">
          <Zap className="w-7 h-7 text-orange-500" />
          <span className="font-black text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>BIDSENTRY</span>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`
                flex items-center gap-3 px-4 py-3 rounded-sm transition-all
                ${isActive(item.path, item.exact) 
                  ? 'bg-orange-50 text-orange-600 font-semibold' 
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
              `}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span>{item.label}</span>
              {isActive(item.path, item.exact) && (
                <ChevronRight className="w-4 h-4 ml-auto" />
              )}
            </Link>
          ))}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <div className="px-4 py-2 mb-2">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-red-600"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <BetaBanner />
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
